import { formatPeriodCell, isPeriodLikeValue } from '@common/utils/formatPeriodCell';

export type CellValue = string | number | boolean | null | undefined;

const BOM = '\uFEFF';
const CRLF = '\r\n';
const MAX_CHUNK_CHARS = 64 * 1024 * 1024;

const escapeCsvField = (value: CellValue): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const str = String(value);

  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
};

const rowToCsvLine = (row: CellValue[]): string =>
  row.map(escapeCsvField).join(',');

export const gridToCsv = (rows: CellValue[][]): string => {
  const lines = rows.map(rowToCsvLine);
  return BOM + lines.join(CRLF) + CRLF;
};

export const gridToCsvBuffer = (rows: CellValue[][]): Buffer => {
  const chunks: Buffer[] = [Buffer.from(BOM, 'utf-8')];
  let pending: string[] = [];
  let pendingChars = 0;
  let isFirstFlush = true;

  const flush = (): void => {
    if (pending.length === 0) {
      return;
    }
    const joined = pending.join(CRLF);
    chunks.push(Buffer.from(isFirstFlush ? joined : CRLF + joined, 'utf-8'));
    isFirstFlush = false;
    pending = [];
    pendingChars = 0;
  };

  rows.forEach(row => {
    const line = rowToCsvLine(row);
    pending.push(line);
    pendingChars += line.length + CRLF.length;
    if (pendingChars >= MAX_CHUNK_CHARS) {
      flush();
    }
  });
  flush();

  chunks.push(Buffer.from(CRLF, 'utf-8'));
  return Buffer.concat(chunks);
};

const normalizeCell = (value: CellValue): CellValue => {
  if (!isPeriodLikeValue(value)) {
    return value;
  }
  const normalized = formatPeriodCell(value);
  return normalized ?? value;
};

export const normalizePeriodCells = (rows: CellValue[][]): CellValue[][] =>
  rows.map(row => row.map(normalizeCell));

export type CsvSheet = {
  name: string;
  content: string;
};

export type CsvBufferSheet = {
  name: string;
  content: Buffer;
};
